import React from "react";
import { TextField, Button, Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import { API_URL } from "../App";
import { pl } from "date-fns/locale";
import { UserDataContext } from "../components/UserDataContext";
import { useState, useEffect, useContext } from "react";
import Alerts from "../components/Alerts";
import { marginBottom } from "../App";
import { Row, Col } from "react-bootstrap";
import ParamWeightChart from "../components/ParamWeightChart";

const validationSchema = Yup.object().shape({
  decimalField: Yup.number()
    .typeError("Musi być liczbą w formacie XX.X")
    .test(
      "is-decimal",
      "Musi mieć maksymalnie 1 miejsce po przecinku",
      (value) => /^\d+(\.\d{1})?$/.test(value)
    )
    .required("Wymagane pole"),
  dateField: Yup.date().required("Data jest wymagana"),
});

const FormWithDecimalAndDate = () => {
  const [defaultWeight, setDefaultWeight] = useState();
  const { userData } = useContext(UserDataContext);
  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({ date: [], weight: [] }); // Domyślna wartość

  const handleSubmit = async (values) => {
    const formattedValues = {
      ...values,
      dateField: dayjs(values.dateField).format("YYYY-MM-DD"),
    };

    try {
      const response = await axios.post(
        API_URL + `/addweight/${userData.cookie}`,
        formattedValues
      );
      setError(response.data.error_msg);
    } catch (error) {
      console.error("Błąd wysyłania danych:", error);
    }
  };

  const today = new Date();

  useEffect(() => {
    const fetchData = async () => {
      try {
        let url = API_URL + `/getweight/${userData.cookie}`;
        const response = await axios.get(url);
        if (
          response.data &&
          Array.isArray(response.data.date) &&
          Array.isArray(response.data.weight)
        ) {
          const convertedDates = response.data.date.map((date) =>
            new Date(date).getTime()
          );
          setDefaultWeight(
            response.data.weight[response.data.weight.length - 1]
          );
          setData({ date: convertedDates, weight: response.data.weight });
        }
      } catch (error) {
        console.error("Błąd wysyłania danych:", error);
      }
    };

    if (userData) {
      fetchData();
    }
  }, [userData, defaultWeight]);

  const initialValues = {
    decimalField: defaultWeight || "",
    dateField: dayjs(),
  };

  useEffect(() => {
    if (error === 200) {
      setApiResponse({
        title: "Sukces",
        severity: "success",
        info: "Dane zapisane",
      });
      setShowApiResponse(true);
    }
    if (error === 567) {
      setApiResponse({
        title: "Błąd",
        severity: "error",
        info: "W tym dniu już została zapisana waga.",
      });
      setShowApiResponse(true);
    }
  }, [error]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      {showApiResponse && (
        <Row style={{ marginBottom }}>
          <Alerts data={apiResponse} />
        </Row>
      )}
      <h5>Rejestracja wagi.</h5>
      <h6>Tworzenie wykresu obrazującego zmianę wagi w czasie.</h6>
      <Row style={{ marginBottom }}>
        <Col md={3}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap={2} width={300}>
                  <TextField
                    label="Ostatnio zmierzona waga"
                    name="decimalField"
                    value={values.decimalField}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.decimalField && Boolean(errors.decimalField)}
                    helperText={touched.decimalField && errors.decimalField}
                    fullWidth
                    type="text"
                    autoComplete="off"
                  />
                  <DatePicker
                    label="Wybierz datę"
                    value={values.dateField}
                    onChange={(value) => setFieldValue("dateField", value)}
                    format="yyyy-MM-dd"
                    slotProps={{ textField: { variant: "outlined" } }}
                    textField={(params) => (
                      <TextField
                        {...params}
                        error={touched.dateField && Boolean(errors.dateField)}
                        helperText={touched.dateField && errors.dateField}
                        fullWidth
                      />
                    )}
                  />
                  <Button variant="contained" color="primary" type="submit">
                    Zapisz
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
      <Row>
        <Col>
          {data.date.length > 0 && data.weight.length > 0 && (
            <ParamWeightChart
              xAxis={data.date.map((date) => dayjs(date).valueOf())}
              series={data.weight}
            />
          )}
        </Col>
      </Row>
    </LocalizationProvider>
  );
};

export default FormWithDecimalAndDate;
